/**
 * Skeleton — ачаалж буй үеийн орлуулагч.
 *
 * ЗАРЧИМ: skeleton нь бодит агуулгын БАЙРЛАЛЫГ дуурайна. Дан эргэлдэгч
 * тавьбал өгөгдөл ирэх мөчид бүх зүйл үсэрч (layout shift), ажилтан
 * дарж байсан товчоо алддаг.
 *
 * Хөдөлгөөнийг `prefers-reduced-motion` дарна — `index.css` дахь base
 * дүрэм animation-ийг зогсооно.
 */

import type { ReactNode } from 'react';

/** Ганц тэгш өнцөгт. Хэмжээг Tailwind class-аар өгнө. */
export function Skeleton({ className = '' }: { className?: string }) {
  return <span className={`block animate-pulse rounded-full bg-track ${className}`} aria-hidden />;
}

/**
 * Мөр мөрөөр текст. Сүүлийн мөр нь богино — бодит догол мөр шиг.
 * Кирилл текст англиас урт тул мөрийг өргөн авна.
 */
export function SkeletonText({ lines = 3, className = '' }: { lines?: number; className?: string }) {
  return (
    <span className={`flex flex-col gap-2 ${className}`} aria-hidden>
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className={`h-3 ${i === lines - 1 ? 'w-3/5' : 'w-full'}`} />
      ))}
    </span>
  );
}

/**
 * Ачаалж буй бүс. `aria-busy` + `aria-live` нь дэлгэц уншигчид
 * «одоо ачаалж байна» гэдгийг хэлнэ — чимээгүй хоосон блок үлдэхгүй.
 */
export function SkeletonRegion({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div aria-busy="true" aria-live="polite">
      <span className="sr-only">{label}</span>
      {children}
    </div>
  );
}

/** Картын хүрээтэй skeleton — StatCard-ийн байрлалыг давтана. */
export function SkeletonStatCard() {
  return (
    <div className="flex flex-col gap-2.5 rounded-card border border-hair bg-panel px-4 py-3.5">
      <Skeleton className="h-3 w-24" />
      <Skeleton className="h-6 w-20" />
      <Skeleton className="h-3 w-32" />
      <Skeleton className="mt-0.5 h-[5px] w-full" />
    </div>
  );
}

export function SkeletonStatGrid({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
      {Array.from({ length: count }, (_, i) => (
        <SkeletonStatCard key={i} />
      ))}
    </div>
  );
}

/** Хүснэгтийн орлуулагч — толгой + мөрүүд. */
export function SkeletonTable({ rows = 5, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="flex flex-col" aria-hidden>
      <div className="flex gap-3 border-b border-hair px-3 py-2.5">
        {Array.from({ length: cols }, (_, i) => (
          <Skeleton key={i} className={`h-2.5 ${i === 0 ? 'w-32' : 'flex-1'}`} />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="flex gap-3 border-b border-hair/70 px-3 py-3">
          {Array.from({ length: cols }, (_, i) => (
            <Skeleton key={i} className={`h-3 ${i === 0 ? 'w-32' : 'flex-1'}`} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Ерөнхий картын орлуулагч — гарчиг + агуулга. */
export function SkeletonCard({ lines = 4, className = '' }: { lines?: number; className?: string }) {
  return (
    <div
      className={`flex flex-col gap-3.5 rounded-card border border-hair bg-panel px-card py-4 ${className}`}
    >
      <Skeleton className="h-4 w-40" />
      <SkeletonText lines={lines} />
    </div>
  );
}
